

import React, { useState } from 'react';
import { KTSVG } from 'src/_metronic/helpers';
import { Modal } from 'react-bootstrap-v5';

type ServiceRequest = {
  id: number;
  reservation_id: number;
  type: string;
  description: string;
  status: string;
  priority: string;
  requested_at: string;
  guest_name: string;
  room_id: number;
  room_number?: string;
};

type Props = {
  className?: string;
  requests: ServiceRequest[];
  isLoading?: boolean;
};

const ServiceRequestsList: React.FC<Props> = ({ className = '', requests = [], isLoading = false }) => {
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Sayfalama hesaplamaları
  const totalPages = Math.ceil((requests?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRequests = requests?.slice(startIndex, endIndex) || [];

  const handleShowDetails = (request: ServiceRequest) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const getTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'room-service': 'Oda Servisi',
      'housekeeping': 'Temizlik',
      'maintenance': 'Bakım',
      'laundry': 'Çamaşırhane',
      'wake-up-call': 'Uyandırma',
      'transportation': 'Ulaşım',
      'spa': 'Spa',
      'extra-amenities': 'Ekstra İkramlar',
      'special-diet': 'Özel Diyet',
    };
    return types[type] || type;
  };

  const getPriorityLabel = (priority: string) => {
    const priorities: Record<string, string> = {
      'urgent': 'Acil',
      'high': 'Yüksek',
      'normal': 'Normal',
      'low': 'Düşük',
    };
    return priorities[priority] || priority;
  };
  return (
    <>
      <div className={`card ${className}`}>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Bekleyen Hizmet Talepleri</span>
            <span className='text-muted fw-semibold fs-7'>
              {isLoading ? 'Yükleniyor...' : `${requests?.length || 0} talep bekliyor`}
            </span>
          </h3>
        </div>
        <div className='card-body py-3'>
          {isLoading ? (
            <div className='d-flex justify-content-center align-items-center py-10'>
              <span className='spinner-border spinner-border-lg' role='status' />
            </div>
          ) : requests && requests.length > 0 ? (
            <>
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='min-w-150px'>Misafir</th>
                      <th className='min-w-140px'>Talep Türü</th>
                      <th className='min-w-100px'>Öncelik</th>
                      <th className='min-w-120px'>Durum</th>
                      <th className='min-w-100px text-end'>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRequests.map((request: ServiceRequest) => (
                    <tr key={request.id}>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='symbol symbol-45px me-5'>
                            <span className='symbol-label bg-light-primary text-primary fw-bold'>
                              {request.room_id}
                            </span>
                          </div>
                          <div className='d-flex justify-content-start flex-column'>
                            <span className='text-dark fw-bold fs-6'>{request.guest_name}</span>
                            <span className='text-muted fw-semibold text-muted d-block fs-7'>
                              Oda {request.room_number || request.room_id}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className='text-dark fw-semibold d-block fs-7'>{getTypeLabel(request.type)}</span>
                      </td>
                      <td>
                        <span className={`badge badge-light-${getPriorityColor(request.priority)}`}>
                          {getPriorityLabel(request.priority)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-light-${getStatusColor(request.status)}`}>
                          {getStatusLabel(request.status)}
                        </span>
                      </td>
                      <td className='text-end'>
                        <button
                          className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                          onClick={() => handleShowDetails(request)}
                          title='Detayları Görüntüle'
                        >
                          <KTSVG path='/media/icons/duotone/General/Visible.svg' className='svg-icon-3' />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className='d-flex justify-content-between align-items-center mt-5'>
                <div className='text-muted fs-7'>
                  Gösterilen: {startIndex + 1} - {Math.min(endIndex, requests.length)} / {requests.length}
                </div>
                <div className='d-flex gap-2'>
                  <button
                    className='btn btn-sm btn-light'
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className='bi bi-chevron-left'></i>
                    Önceki
                  </button>
                  
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    // Sadece mevcut sayfaya yakın sayfaları göster
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          className={`btn btn-sm ${currentPage === page ? 'btn-primary' : 'btn-light'}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className='align-self-center'>...</span>;
                    }
                    return null;
                  })}
                  
                  <button
                    className='btn btn-sm btn-light'
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Sonraki
                    <i className='bi bi-chevron-right'></i>
                  </button>
                </div>
              </div>
            )}
          </>
          ) : (
            <div className='d-flex flex-column align-items-center py-10'>
              <KTSVG path='/media/icons/duotone/Home/Building.svg' className='svg-icon-4x svg-icon-muted mb-3' />
              <span className='text-muted fs-5'>Bekleyen hizmet talebi bulunmuyor</span>
            </div>
          )}
        </div>
      </div>

      {/* Detay Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Hizmet Talebi Detayları</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <div className='row mb-5'>
                <div className='col-md-6'>
                  <label className='fw-bold text-muted d-block mb-2'>Misafir Adı</label>
                  <span className='fs-5 fw-semibold text-dark'>{selectedRequest.guest_name}</span>
                </div>
                <div className='col-md-6'>
                  <label className='fw-bold text-muted d-block mb-2'>Oda Numarası</label>
                  <span className='fs-5 fw-semibold text-dark'>Oda {selectedRequest.room_number || selectedRequest.room_id}</span>
                </div>
              </div>

              <div className='row mb-5'>
                <div className='col-md-6'>
                  <label className='fw-bold text-muted d-block mb-2'>Talep Türü</label>
                  <span className='fs-5 fw-semibold text-dark'>{getTypeLabel(selectedRequest.type)}</span>
                </div>
                <div className='col-md-6'>
                  <label className='fw-bold text-muted d-block mb-2'>Rezervasyon ID</label>
                  <span className='fs-5 fw-semibold text-dark'>#{selectedRequest.reservation_id}</span>
                </div>
              </div>

              <div className='row mb-5'>
                <div className='col-md-6'>
                  <label className='fw-bold text-muted d-block mb-2'>Öncelik</label>
                  <span className={`badge badge-light-${getPriorityColor(selectedRequest.priority)} fs-6`}>
                    {getPriorityLabel(selectedRequest.priority)}
                  </span>
                </div>
                <div className='col-md-6'>
                  <label className='fw-bold text-muted d-block mb-2'>Durum</label>
                  <span className={`badge badge-light-${getStatusColor(selectedRequest.status)} fs-6`}>
                    {getStatusLabel(selectedRequest.status)}
                  </span>
                </div>
              </div>

              <div className='row mb-5'>
                <div className='col-12'>
                  <label className='fw-bold text-muted d-block mb-2'>Talep Zamanı</label>
                  <span className='fs-6 text-dark'>{formatDate(selectedRequest.requested_at)}</span>
                </div>
              </div>

              <div className='row'>
                <div className='col-12'>
                  <label className='fw-bold text-muted d-block mb-2'>Açıklama</label>
                  <div className='p-4 bg-light rounded'>
                    <span className='fs-6 text-dark'>{selectedRequest.description}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-light' onClick={handleCloseModal}>
            Kapat
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const getStatusLabel = (status: string) => {
  const statuses: Record<string, string> = {
    'pending': 'Bekliyor',
    'assigned': 'Atandı',
    'in-progress': 'İşlemde',
    'completed': 'Tamamlandı',
    'cancelled': 'İptal',
  };
  return statuses[status] || status;
};

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending':
    case 'bekliyor':
      return 'warning';
    case 'assigned':
    case 'atandı':
      return 'info';
    case 'in-progress':
    case 'işlemde':
      return 'primary';
    case 'completed':
    case 'tamamlandı':
      return 'success';
    case 'cancelled':
    case 'iptal':
      return 'danger';
    default:
      return 'secondary';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'urgent':
    case 'acil':
      return 'danger';
    case 'high':
    case 'yüksek':
      return 'warning';
    case 'normal':
      return 'primary';
    case 'low':
    case 'düşük':
      return 'secondary';
    default:
      return 'secondary';
  }
};

export default ServiceRequestsList;
